import { TellescopeModelFromName, type useTellescopeMutation, type ReturnForMethod } from "@tellescope/tanstack-query";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import { useCallback, useMemo } from "react";
import { FieldChangeOptions, FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormField,
  FormFieldOptions,
  FormResponseValue,
} from "@tellescope/types-models";
import { useTellescopeFormCore, type LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import type { InterpolateFieldText } from "../use-tellescope-form-core/use-interpolate-field-text";
import { useGetFieldProps, type RepeatInputProps } from "./use-get-field-props";
import { useFormNavigation } from "./use-form-navigation";
import { useAutoAdvance } from "./use-auto-advance";
import { useSaveFieldResponse } from "./use-save-field-response";
import { useEnduserSubmitForm } from "./use-enduser-submit-form";
import { isNextDisabled } from "./is-next-disabled";
import { blockingErrorsOf, resolveFieldStackEntries, type BlockingFieldError } from "./resolve-field-stack-entries";
import type { FormShallowStoreHook, SubmitPhase } from "../../store";

export interface UseTellescopeEnduserFormArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  automationStepId?: string;
  startingFieldId?: string;
  existingResponses?: FormResponseValue[];
  urlLogicValue?: string;
  accessCode: string;
  enduser: TellescopeModelFromName<"endusers"> | Enduser;
  publicIdentifier?: string;
  useFormShallowStore: FormShallowStoreHook;
}

export interface UseTellescopeEnduserFormNoRoot {
  hasNoRootField: true;
}

export const isFillableEnduserForm = (
  enduserForm: UseTellescopeEnduserFormReturn | UseTellescopeEnduserFormNoRoot | null
): enduserForm is UseTellescopeEnduserFormReturn => !!enduserForm && !("hasNoRootField" in enduserForm);

export interface UseTellescopeEnduserFormReturn {
  currentResponses: LocalTellescopeFormResponseMap;
  interpolateFieldText: InterpolateFieldText;
  validateField: <T extends keyof AnswerForType = keyof AnswerForType>(args: {
    fieldId: string;
    valueOverride?: AnswerForType[T];
  }) => string | null;
  validateAllFields: () => boolean;
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  setFieldValue: <T extends FormTypesWithValues>(
    fieldId: string,
    value: AnswerForType[T],
    changeOptions?: FieldChangeOptions
  ) => void;
  saveResponse: () => Promise<void>;
  submitForm: () => Promise<ReturnForMethod<"form_responses", "submit_form_response">>;
  submitFormMutation: ReturnType<typeof useTellescopeMutation<"form_responses", "submit_form_response">>;
  submitPhase: SubmitPhase;
  isAutoSubmitting: boolean;
  autoSubmitted: boolean;
  autoSubmitError: boolean;
  serverRedirectUrl: string | undefined;
  uploadProgress: { completed: number; total: number };
  form: TellescopeModelFromName<"forms"> | undefined;
  fieldErrors: Record<string, string | null>;
  hasBlockingFieldError: boolean;
  blockingFieldErrors: BlockingFieldError[];
  touchedFields: Set<string>;
  handleFieldBlur: (fieldId: string) => void;
  getFieldProps: <T extends FormTypesWithValues = FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T], changeOptions?: FieldChangeOptions) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
    fullZIP?: boolean;
    repeat?: RepeatInputProps;
    disabled?: boolean;
    pendingFiles?: File[];
    onPendingFilesChange?: (files: File[]) => void;
  } | null;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  isComplete: boolean;
  isSubmitted: boolean;
  fieldStack: FormFieldNode[];
  activeField: FormFieldNode;
  currentFieldResponse: TellescopeFieldResponse | undefined;
  goToNextField: () => void;
  goToPreviousField: () => void;
  goToField: (fieldId: string) => void;
  isNextDisabled: boolean;
  isPreviousDisabled: boolean;
  showSubmit: boolean;
  currentPageIndex: number;
  totalPages: number;
  progress: number;
  getNumberOfRemainingPages: () => number;
  saveFieldResponse: () => Promise<void>;
  fieldsArr: TellescopeModelFromName<"form_fields">[];
  logicOptions: NextFieldLogicOptions;
  applyDatabasePrepopulation: (records: DatabaseRecord[]) => void;
  setCustomerId: (customerId: string) => void;
  bookedAppointmentUrl: string | undefined;
  setBookedAppointmentUrl: (bookedAppointmentUrl: string | undefined) => void;
}

export const useTellescopeEnduserForm = ({
  formId,
  formTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  automationStepId,
  startingFieldId,
  existingResponses,
  urlLogicValue,
  accessCode,
  enduser,
  publicIdentifier,
  useFormShallowStore,
}: UseTellescopeEnduserFormArgs): UseTellescopeEnduserFormReturn | UseTellescopeEnduserFormNoRoot | null => {
  const core = useTellescopeFormCore({
    formId,
    formTitle,
    responseId,
    enduserId,
    parentResponseId,
    rootResponseId,
    existingResponses,
    urlLogicValue,
    accessCode,
    enduser,
    publicIdentifier,
    useFormShallowStore,
  });

  const { response } = core.enduserResponseInfoQuery.data ?? {};
  const form = core.form;

  const fieldsMap = useMemo(() => core.fields?.fieldsMap ?? {}, [core.fields?.fieldsMap]);

  const {
    saveActiveFieldAndSubFields,
    saveImmediateFieldResponse,
    saveFieldResponse: saveFieldResponseFor,
  } = useSaveFieldResponse({ core, responseId, responseAccessCode: response?.accessCode });

  const {
    fieldStack,
    activeField,
    hasNoRootField,
    goToField,
    pageIndex,
    totalPages,
    progress,
    goToNextFieldInternal,
    goToPreviousField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  } = useFormNavigation({
    fieldsArr: core.fields?.fieldsArr,
    fieldsMap,
    subFieldIds: core.subFieldIds,
    currentResponses: core.currentResponses,
    responsesMap: core.responsesMap,
    logicOptions: core.logicOptions,
    existingResponses,
    startingFieldId,
    onBeforeAdvance: saveActiveFieldAndSubFields,
    responseId: responseId ?? response?.id,
    responseAccessCode: response?.accessCode,
    useFormShallowStore,
  });

  const entries = useMemo(
    () => resolveFieldStackEntries(fieldStack, core.validateFieldValue),
    [fieldStack, core.validateFieldValue]
  );
  const activeEntry = entries.find((entry) => entry.node.value.id === activeField?.value.id);

  const fieldErrors = useMemo(
    () =>
      entries.reduce<Record<string, string | null>>(
        (acc, entry) => ({ ...acc, [entry.node.value.id]: entry.error, ...entry.subFieldErrors }),
        {}
      ),
    [entries]
  );

  const {
    setFieldValueWithAutoAdvance,
    submitFormRef,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    serverRedirectUrl,
  } = useAutoAdvance({
    setFieldValue: core.setFieldValue,
    fieldsMap,
    activeEntry,
    goToNextFieldInternal,
    saveImmediateFieldResponse,
    useFormShallowStore,
  });

  const goToNextField = useCallback(() => {
    if (!activeEntry || activeEntry.error) return;
    goToNextFieldInternal();
  }, [activeEntry, goToNextFieldInternal]);

  const nextDisabled = isNextDisabled(activeEntry);

  const saveFieldResponse = useCallback(() => saveFieldResponseFor(activeField), [saveFieldResponseFor, activeField]);

  const submitForm = useEnduserSubmitForm({
    core,
    activeField,
    fieldStack,
    fieldsMap,
    existingResponses,
    response,
    formId,
    enduserId,
    parentResponseId,
    rootResponseId,
    automationStepId,
    useFormShallowStore,
  });

  submitFormRef.current = submitForm;

  const blockingFieldErrors = useMemo(() => blockingErrorsOf(entries, fieldsMap), [entries, fieldsMap]);
  const hasBlockingFieldError = blockingFieldErrors.length > 0;

  const getFieldProps = useGetFieldProps({ core, fieldErrors, setFieldValueWithAutoAdvance });

  const isSubmitted = !!response?.submittedAt || core.submitFormMutation.isSuccess;

  if (!activeField) return hasNoRootField ? { hasNoRootField: true } : null;

  return {
    currentResponses: core.currentResponses,
    interpolateFieldText: core.interpolateFieldText,
    validateField: core.validateField,
    validateAllFields: core.validateAllFields,
    visibleFields: core.visibleFields,
    setFieldValue: setFieldValueWithAutoAdvance,
    saveResponse: core.saveResponse,
    submitForm,
    submitFormMutation: core.submitFormMutation,
    submitPhase: core.submitPhase,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    serverRedirectUrl,
    uploadProgress: core.uploadProgress,
    form,
    fieldErrors,
    hasBlockingFieldError,
    blockingFieldErrors,
    touchedFields: core.touchedFields,
    handleFieldBlur: core.handleFieldBlur,
    getFieldProps,
    applyDatabasePrepopulation: core.applyDatabasePrepopulation,
    setCustomerId: core.setCustomerId,
    bookedAppointmentUrl: core.bookedAppointmentUrl,
    setBookedAppointmentUrl: core.setBookedAppointmentUrl,
    enduser: core.enduser,
    isComplete: core.isComplete,
    isSubmitted,
    fieldStack,
    activeField,
    currentFieldResponse: core.currentResponses[activeField.value.id],
    goToNextField,
    goToPreviousField,
    goToField,
    isNextDisabled: nextDisabled,
    isPreviousDisabled,
    showSubmit,
    currentPageIndex: pageIndex,
    totalPages,
    progress,
    getNumberOfRemainingPages,
    saveFieldResponse,
    fieldsArr: core.fields?.fieldsArr ?? [],
    logicOptions: core.logicOptions,
  };
};
